import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError, eur, offerTotals } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, User, Trash2, Mail, Phone, Globe, MapPin, Pencil } from "lucide-react";

const emptyContact = { name: "", role: "", email: "", phone: "" };
const STAGE_COLORS = { Lead: "#94A3B8", Contatto: "#0EA5E9", Preventivo: "#F59E0B", Vinto: "#10B981", Perso: "#EF4444" };

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [edit, setEdit] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [open, setOpen] = useState(false);
  const [cForm, setCForm] = useState(emptyContact);

  const load = useCallback(() => {
    api.get(`/companies/${id}`).then((r) => setCompany(r.data));
    api.get(`/contacts?company_id=${id}`).then((r) => setContacts(r.data));
    api.get("/offers").then((r) => setOffers(r.data.filter((o) => o.company_id === id)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveContact = async () => {
    if (!cForm.name.trim()) { toast.error("Nome referente obbligatorio"); return; }
    try {
      await api.post("/contacts", { ...cForm, company_id: id });
      toast.success("Referente aggiunto"); setOpen(false); setCForm(emptyContact); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delContact = async (cid) => { await api.delete(`/contacts/${cid}`); toast.success("Referente eliminato"); load(); };

  const saveCompany = async () => {
    try { await api.put(`/companies/${id}`, edit); toast.success("Azienda aggiornata"); setEdit(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delCompany = async () => { await api.delete(`/companies/${id}`); toast.success("Azienda eliminata"); navigate("/aziende"); };

  if (!company) return <div className="p-10 font-mono-label text-sm text-slate-400">CARICAMENTO...</div>;

  return (
    <div className="p-10">
      <button data-testid="back-btn" onClick={() => navigate("/aziende")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#0052CC] transition-colors duration-200 mb-6">
        <ArrowLeft className="w-4 h-4" /> Torna ai contatti
      </button>

      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-label text-[10px] text-[#0052CC] mb-2">{company.industry || "AZIENDA"}</div>
          <h1 className="font-display font-bold text-3xl tracking-tight text-slate-900">{company.name}</h1>
        </div>
        <div className="flex gap-2">
          <Button data-testid="edit-company-btn" variant="outline" onClick={() => setEdit(company)} className="rounded-sm"><Pencil className="w-4 h-4 mr-2" />Modifica</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="delete-company-btn" variant="outline" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-sm">
              <AlertDialogHeader><AlertDialogTitle>Eliminare {company.name}?</AlertDialogTitle>
                <AlertDialogDescription>Verranno eliminati anche i referenti associati. Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
                <AlertDialogAction data-testid="confirm-delete-company" onClick={delCompany} className="bg-red-600 hover:bg-red-700 rounded-sm">Elimina</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <div className="font-mono-label text-[10px] text-slate-500 mb-4">DETTAGLI</div>
          <div className="space-y-3 text-sm">
            {company.vat && <div className="text-slate-600"><span className="font-mono-label text-[10px] text-slate-400 block">P.IVA</span>{company.vat}</div>}
            {company.email && <div className="flex items-center gap-2 text-slate-700"><Mail className="w-4 h-4 text-slate-400" />{company.email}</div>}
            {company.phone && <div className="flex items-center gap-2 text-slate-700"><Phone className="w-4 h-4 text-slate-400" />{company.phone}</div>}
            {company.website && <div className="flex items-center gap-2 text-slate-700"><Globe className="w-4 h-4 text-slate-400" />{company.website}</div>}
            {(company.address || company.city) && <div className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-slate-400" />{[company.address, company.city].filter(Boolean).join(", ")}</div>}
            {company.notes && <div className="pt-3 border-t border-slate-100 text-slate-600">{company.notes}</div>}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono-label text-[10px] text-slate-500">REFERENTI</div>
            <Button data-testid="add-contact-btn" size="sm" variant="ghost" onClick={() => setOpen(true)} className="h-7 text-[#0052CC] hover:bg-blue-50"><Plus className="w-4 h-4 mr-1" />Aggiungi</Button>
          </div>
          {contacts.length === 0 ? <p className="text-sm text-slate-400">Nessun referente.</p> : (
            <div className="space-y-3">
              {contacts.map((c) => (
                <div key={c.id} data-testid={`contact-${c.id}`} className="flex items-start justify-between border border-slate-100 rounded-sm p-3">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-sm bg-slate-100 flex items-center justify-center text-slate-500"><User className="w-4 h-4" /></div>
                    <div>
                      <div className="font-medium text-sm text-slate-900">{c.name}</div>
                      <div className="text-xs text-slate-500">{c.role}</div>
                      {c.email && <div className="text-xs text-slate-500">{c.email}</div>}
                      {c.phone && <div className="text-xs text-slate-500">{c.phone}</div>}
                    </div>
                  </div>
                  <button data-testid={`del-contact-${c.id}`} onClick={() => delContact(c.id)} className="text-slate-300 hover:text-red-500 transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <div className="font-mono-label text-[10px] text-slate-500 mb-4">OFFERTE COLLEGATE</div>
          {offers.length === 0 ? <p className="text-sm text-slate-400">Nessuna offerta.</p> : (
            <div className="space-y-2">
              {offers.map((o) => (
                <Link key={o.id} to={`/offerte/${o.id}`} data-testid={`company-offer-${o.id}`}
                  className="flex items-center justify-between border border-slate-100 rounded-sm p-3 hover:bg-slate-50 transition-colors duration-150">
                  <div>
                    <div className="text-sm font-medium text-slate-900">{o.title}</div>
                    <div className="font-mono-label text-[9px] text-slate-400">{o.number}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-900">{eur(offerTotals(o).total)}</div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm text-white" style={{ background: STAGE_COLORS[o.stage] }}>{o.stage}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display">Nuovo Referente</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {[["name", "Nome *"], ["role", "Ruolo"], ["email", "Email"], ["phone", "Telefono"]].map(([k, l]) => (
              <div key={k}><Label className="font-mono-label text-[10px] text-slate-500">{l}</Label>
                <Input data-testid={`contact-${k}-input`} value={cForm[k]} onChange={(e) => setCForm({ ...cForm, [k]: e.target.value })} className="mt-1.5 rounded-sm" /></div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Annulla</Button>
            <Button data-testid="save-contact-btn" onClick={saveContact} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Modifica Azienda</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-2 gap-4 py-2">
              {[["name", "Nome *", "col-span-2"], ["industry", "Settore", ""], ["vat", "P.IVA", ""], ["email", "Email", ""], ["phone", "Telefono", ""], ["website", "Sito web", ""], ["city", "Città", ""], ["address", "Indirizzo", "col-span-2"]].map(([k, l, cls]) => (
                <div key={k} className={cls}><Label className="font-mono-label text-[10px] text-slate-500">{l}</Label>
                  <Input data-testid={`edit-company-${k}`} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className="mt-1.5 rounded-sm" /></div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} className="rounded-sm">Annulla</Button>
            <Button data-testid="save-edit-company-btn" onClick={saveCompany} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
